import {
  beginSubmissionUpload,
  completeSubmissionUpload,
  discardPendingSubmission,
  isDomainError,
  listExamSubmissions,
  removeSubmission,
  requireSubmissionAccess,
} from '@aimfora/db/repositories'
import { PDF_LIMITS } from '@aimfora/pdf'
import { isSubmissionKeyOf, newSubmissionKey, PDF_TYPE } from '@aimfora/storage'
import { notFound } from '@tanstack/react-router'
import { createServerFn } from '@tanstack/react-start'
import { examIdSchema, requestSubmissionUploadSchema, submissionIdSchema } from '~/lib/domain'
import type { SubmissionCompletion, SubmissionUploadTicket } from '~/lib/views'
import { requireOrg } from '~/middleware/auth'
import { limitUploads } from '~/middleware/rate-limit'
import { enqueueSubmissionProcessing } from '~/server/jobs.server'
import { getLogger } from '~/server/logger.server'
import { RequestRejected } from '~/server/rejected.server'
import { getStorage } from '~/server/storage.server'
import { verifyUploadedSubmission } from '~/server/submission-upload.server'
import { toExamSubmissionsView, toSubmissionView } from '~/server/views.server'

/**
 * Student work. The browser removes the student's name from the PDF and uploads only that
 * redacted file. Nothing here ever receives a file name, a student's real name or the original:
 * the request carries ids and a size, and the stored object has a key the server derived.
 *
 * What the server can check without a name is that the file is exactly the flat format the
 * browser writes (see `inspectRedactedPdf`). It cannot check what the picture shows.
 */

async function deleteQuietly(keys: readonly string[]) {
  if (keys.length === 0) return
  try {
    await getStorage().deleteMany(keys)
  } catch (error) {
    getLogger().warn({ err: error, count: keys.length }, 'could not delete objects from storage')
  }
}

/** Every student of the exam's class with their submission, for the exam's "Besvarelser" step. */
export const getExamSubmissions = createServerFn({ method: 'GET' })
  .middleware([requireOrg])
  .validator(examIdSchema)
  .handler(async ({ context, data }) => {
    try {
      return toExamSubmissionsView(
        context.scope,
        await listExamSubmissions(context.scope, data.examId),
      )
    } catch (error) {
      // A missing exam, and one the caller may not see, are both a plain "not found"
      if (isDomainError(error) && error.code === 'not_found') throw notFound()
      throw error
    }
  })

export const requestSubmissionUpload = createServerFn({ method: 'POST' })
  .middleware([requireOrg, limitUploads])
  .validator(requestSubmissionUploadSchema)
  .handler(async ({ context, data }): Promise<SubmissionUploadTicket> => {
    if (data.sizeBytes > PDF_LIMITS.maxStoredBytes) throw new RequestRejected('upload_too_large')

    const { orgId } = context.scope.ctx
    const key = newSubmissionKey({ orgId, examId: data.examId })
    const { submission, abandonedKeys } = await beginSubmissionUpload(context.scope, {
      examId: data.examId,
      studentId: data.studentId,
      sizeBytes: data.sizeBytes,
      s3Key: key,
    })
    await deleteQuietly(abandonedKeys)

    const ticket = await getStorage().presignUpload({
      key,
      contentType: PDF_TYPE,
      sizeBytes: data.sizeBytes,
    })
    return {
      submissionId: submission.id,
      url: ticket.url,
      method: ticket.method,
      headers: ticket.headers,
    }
  })

export const completeSubmissionUploadFn = createServerFn({ method: 'POST' })
  .middleware([requireOrg, limitUploads])
  .validator(submissionIdSchema)
  .handler(async ({ context, data }): Promise<SubmissionCompletion> => {
    const { scope } = context
    const pending = await requireSubmissionAccess(scope, data.submissionId, 'modify')
    // Completing is for an upload in progress. A stored submission is never read back, rejected or
    // deleted from here: its upload link may still work, so its file could have been overwritten
    if (pending.status !== 'pending_upload') throw new RequestRejected('submission_not_pending')
    if (!isSubmissionKeyOf(pending.s3Key, { orgId: scope.ctx.orgId, examId: pending.examId })) {
      throw new Error('storage key does not belong to this exam')
    }

    // Only a row that was really still pending is dropped, and then only its own file
    const discard = async () => {
      const key = await discardPendingSubmission(scope, pending.id)
      if (key) await deleteQuietly([key])
    }

    const reject = async (code: string, detail?: string): Promise<never> => {
      await discard()
      // Only the reason is logged, never anything from the file
      getLogger().info({ submissionId: pending.id, code, detail }, 'submission upload rejected')
      throw new RequestRejected(code)
    }

    // The flat redacted format, with the marker and nothing else in it (ADR 0006)
    const verdict = await verifyUploadedSubmission(getStorage(), pending)
    if (!verdict.ok) return reject(verdict.code, verdict.detail)
    const { inspection } = verdict

    const outcome = await completeSubmissionUpload(scope, pending.id, {
      sha256: verdict.sha256,
      sizeBytes: verdict.bytes.length,
      pageCount: inspection.pageCount,
      hasTextLayer: inspection.hasTextLayer,
      redactionMeta: inspection.marker,
    })

    if (outcome.status === 'duplicate') {
      // The same file is already stored: keep that one, drop this copy and tell the teacher
      await discard()
      return {
        status: 'duplicate',
        sameStudent: outcome.sameStudent,
        studentNumber: outcome.studentNumber,
      }
    }

    await deleteQuietly(outcome.replacedKeys)
    try {
      await enqueueSubmissionProcessing(scope.ctx.orgId, outcome.submission.id)
    } catch (error) {
      // The sweep fails a submission that is never picked up, so the teacher gets a status
      getLogger().error(
        { err: error, submissionId: outcome.submission.id },
        'could not queue submission',
      )
    }
    return {
      status: 'stored',
      submission: toSubmissionView(outcome.submission),
      replaced: outcome.replaced,
    }
  })

export const removeSubmissionFn = createServerFn({ method: 'POST' })
  .middleware([requireOrg])
  .validator(submissionIdSchema)
  .handler(async ({ context, data }) => {
    const key = await removeSubmission(context.scope, data.submissionId)
    await deleteQuietly([key])
  })

/** A short-lived link to the redacted file the teacher stored, so they can see what is kept. */
export const getSubmissionDownloadUrl = createServerFn({ method: 'POST' })
  .middleware([requireOrg])
  .validator(submissionIdSchema)
  .handler(async ({ context, data }) => {
    const row = await requireSubmissionAccess(context.scope, data.submissionId, 'view')
    if (row.status === 'pending_upload') throw new RequestRejected('not_found')
    const url = await getStorage().presignDownload({ key: row.s3Key, fileName: 'besvarelse.pdf' })
    return { url }
  })
